// Resize an image file client-side to a square WebP thumbnail before upload, so
// avatars are stored and served at a fixed, small size regardless of the source photo.

// Above this the decoded bitmap (~width * height * 4 bytes) risks hanging or
// crashing a mobile tab. File size is a poor proxy for this — a modest JPEG can
// still be tens of megapixels — so this checks dimensions, not bytes.
const MAX_SOURCE_MEGAPIXELS = 50;

/**
 * Centre-crops `file` to a square and re-encodes it as WebP (falling back to JPEG
 * on browsers that silently refuse WebP canvas export, e.g. older Safari).
 *
 * @returns {Promise<{ blob: Blob, contentType: string }>}
 */
export async function resizeToWebp(file, { maxDimension = 512, quality = 0.8 } = {}) {
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });

  if (bitmap.width * bitmap.height > MAX_SOURCE_MEGAPIXELS * 1_000_000) {
    bitmap.close();
    throw new Error("Image is too large to process");
  }

  const shortestSide = Math.min(bitmap.width, bitmap.height);
  const cropX = (bitmap.width - shortestSide) / 2;
  const cropY = (bitmap.height - shortestSide) / 2;
  const size = Math.min(maxDimension, shortestSide);

  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    bitmap,
    cropX,
    cropY,
    shortestSide,
    shortestSide,
    0,
    0,
    size,
    size,
  );
  bitmap.close();

  let blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/webp", quality),
  );

  if (!blob || blob.type !== "image/webp") {
    blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality),
    );
  }

  if (!blob) {
    throw new Error("Could not process image");
  }

  return { blob, contentType: blob.type };
}
